import { fetchTransactions, useBranches, useUsers } from "@api";
import { ServerTable, Badge, PageHeader, PageTitle } from "@components";
import { useTableFilters } from "@components/tables/shared";
import { useTableQuery } from "@hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  buildApiQuery,
  formatAmount,
  formatDateTime,
  formatDateTimeForExport,
} from "@utils";
import { useEffect, useState, useMemo, useCallback } from "react";
import { Star, StarFill } from "react-bootstrap-icons";
import { usePaymentRequestLabels } from "@flags";
import { transactionStatusColors, refundStatusLabel } from "../common";
import TransactionDetails from "../displays/TransactionDetails";

const PAGE_SIZE = 100;
const RECONCILED_LABEL = "ReconciledInErp";

// Filterable and switchable, never drawn. The star is pinned to the left edge, so drawing this
// as a column too would show it twice. `filterOnly` keeps it out of the table body while leaving
// it in the preferences, which is what puts it in column customisation and the filter picker —
// `hide` would drop it before it ever got there.
const RECONCILED_FILTER = {
  title: "Reconciled",
  key: "reconciled",
  id: "reconciled",
  filterOnly: true,
  type: "enum",
  values: ["Reconciled", "Not reconciled"],
  render: (x) => x.reconciled,
  renderFilter: (x) => x,
};

const Transactions = () => {
  const queryClient = useQueryClient();
  const labelsEnabled = usePaymentRequestLabels();
  const [payment, setPayment] = useState(null);
  const { data: branches = [] } = useBranches();
  const { data: users = [] } = useUsers();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("transactionDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const memoizedBranches = useMemo(
    () => branches.map((branch) => branch.name) || [],
    [branches],
  );

  const memoizedUsers = useMemo(
    () => users.map((user) => user.name) || [],
    [users],
  );

  const columns = useMemo(
    () => [
      // First, so it heads both the column customisation list and the filter picker — both are
      // ordered by the stored preferences, which take their initial positions from this array.
      ...(labelsEnabled ? [RECONCILED_FILTER] : []),
      {
        title: "Amount",
        key: "amount",
        id: "amount",
        width: "140px",
        render: (x) => formatAmount(x.amount),
        sortFunc: true,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Type",
        key: "transactionType",
        id: "type",
        width: "140px",
        render: (x) => x.transactionType,
        type: "enum",
        values: ["Payment", "Refund"],
      },
      {
        title: "Transaction Date",
        key: "transactionDate",
        id: "paidOn",
        width: "160px",
        type: "date",
        render: (x) => formatDateTime(x.transactionDate),
        sortFunc: true,
      },
      {
        title: "Settlement Date",
        key: "settledOn",
        id: "settledOn",
        width: "160px",
        type: "date",
        render: (x) => formatDateTime(x.settledOn),
        sortFunc: true,
      },
      {
        title: "Customer",
        key: "customerName",
        id: "customerName",
        width: "224px",
        type: "string",
        render: (x) => x.customerName,
        sortFunc: true,
      },
      {
        title: "Status",
        key: "status",
        id: "status",
        width: "180px",
        type: "enum",
        // Wire values are TransactionStatus names (parsed by ToClientTransactionFilter);
        // Initialized is shown under its display label, see refundStatusLabel.
        values: [
          "Paid",
          "Refunded",
          "Pending",
          { value: "Initialized", label: "Awaiting authorisation" },
          "Failed",
          "Cancelled",
        ],
        render: (x) => (
          <Badge color={transactionStatusColors[refundStatusLabel(x.status)]}>
            {refundStatusLabel(x.status)}
          </Badge>
        ),
      },
      {
        title: "Reference",
        key: "paymentReference",
        id: "reference",
        width: "180px",
        type: "string",
        render: (x) => x.paymentReference,
        sortFunc: true,
      },
      {
        title: "Source",
        key: "source",
        id: "source",
        width: "180px",
        type: "enum",
        // Display names produced by the BFF (Formatters.FormatSource) and
        // translated back to PaymentSource values in TransactionsMapper.
        values: [
          "Payment request",
          "Ontik payment portal",
          "eCommerce",
          "Open payment link",
          "POS terminal",
        ],
        render: (x) => x.source,
        renderFilter: (x) => x,
      },
      {
        title: "Method",
        key: "paymentMethod",
        id: "paymentMethod",
        width: "180px",
        type: "enum",
        values: ["Pay by bank", "Card"],
        render: (x) => x.paymentMethod,
        renderFilter: (x) => x,
      },
      {
        title: "Payment type",
        key: "paymentType",
        id: "paymentType",
        width: "180px",
        type: "enum",
        values: ["Order", "Account"],
        render: (x) => x.paymentType,
        renderFilter: (x) => x,
      },
      {
        title: "Branch",
        key: "branch",
        id: "branch",
        width: "120px",
        render: (x) => x.branch,
        filterValues: true,
        type: "enum",
        values: memoizedBranches,
      },
      {
        title: "Requested by",
        key: "requestedByName",
        id: "requestedByName",
        width: "150px",
        render: (x) => x.requestedByName,
        sortFunc: true,
        type: "enum",
        values: memoizedUsers,
      },
      {
        title: "Payment amount",
        key: "paymentAmount",
        id: "paymentAmount",
        width: "180px",
        render: (x) => (x.paymentAmount ? formatAmount(x.paymentAmount) : "-"),
        sortFunc: true,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
      {
        title: "Refunded amount",
        key: "refundedAmount",
        id: "refundedAmount",
        width: "180px",
        render: (x) =>
          x.refundedAmount ? formatAmount(x.refundedAmount) : "-",
        sortFunc: true,
        type: "number",
        filterPrefix: "£",
        right: true,
      },
    ],
    [memoizedBranches, memoizedUsers, labelsEnabled],
  );

  const filterManager = useTableFilters("transactions", columns);
  const { filters } = filterManager;

  const getQueryParams = useCallback(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      filters,
      sort,
      sortDirection,
      searchTerm,
    }),
    [filters, sort, sortDirection, searchTerm, page],
  );

  const query = useTableQuery({
    resource: "transactions",
    fetcher: fetchTransactions,
    getQueryParams,
  });

  // Reset to first page when the result-set scope changes.
  useEffect(() => {
    setPage(1);
  }, [filters, sort, sortDirection, searchTerm]);

  const handleSort = (key) => {
    if (sort === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setSortDirection("desc");
    }
  };

  const reloadData = () => {
    queryClient.invalidateQueries({ queryKey: ["transactions"] });
  };

  const exportData = async () => {
    const query = buildApiQuery({
      filters,
      sort,
      sortDirection,
      searchTerm,
      export: true,
    });
    const result = await fetchTransactions(query);
    // Stamp dates in the user's local time; the API returns them as UTC ISO strings.
    return result.data.map((row) => ({
      ...row,
      transactionDate: formatDateTimeForExport(row.transactionDate),
      settledOn: formatDateTimeForExport(row.settledOn),
    }));
  };

  const tableConfig = useMemo(
    () => ({
      key: "transactions",
      onClick: (x) => setPayment(x),
      colCustomisation: true,
      // Read-only here: the star shows reconciled state at a glance, but marking it happens
      // in the drawer, next to the transaction id the user pastes into the ERP.
      pinnedLeft: labelsEnabled
        ? {
            // Follows the hidden Reconciled column, so turning that off in column
            // customisation hides the star and its filter together.
            id: "reconciled",
            header: (
              <StarFill
                className="size-4 text-zinc-300"
                title="Reconciled in ERP"
              />
            ),
            render: (x) =>
              x.labels?.includes(RECONCILED_LABEL) ? (
                <StarFill
                  className="inline size-4 text-amber-400"
                  title="Reconciled in ERP"
                />
              ) : (
                <Star
                  className="inline size-4 text-zinc-300"
                  title="Not reconciled in ERP"
                />
              ),
          }
        : undefined,
      columns,
    }),
    [columns, labelsEnabled],
  );

  const statistics = [
    {
      label: "Total",
      value: query.data ? formatAmount(query.data.statistics.totalPaid) : null,
    },
    {
      label: "Pay by bank",
      value: query.data ? formatAmount(query.data.statistics.payByBank) : null,
    },
    {
      label: "Card",
      value: query.data ? formatAmount(query.data.statistics.card) : null,
    },
    {
      label: "Refunded",
      value: query.data ? formatAmount(query.data.statistics.refunded) : null,
    },
  ];

  return (
    <>
      <PageHeader>
        <PageTitle>Transactions</PageTitle>
      </PageHeader>
      <ServerTable
        config={tableConfig}
        data={query.data?.data || []}
        loading={query.isLoading || query.isPlaceholderData}
        statistics={statistics}
        results={query.data?.rows || 0}
        page={page}
        setPage={setPage}
        pageSize={PAGE_SIZE}
        filterManager={filterManager}
        sort={sort}
        sortDirection={sortDirection}
        onSort={handleSort}
        searchTerm={searchTerm}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
        exportData={exportData}
      />
      <TransactionDetails
        onClose={() => setPayment(null)}
        id={payment?.paymentId}
        paymentMethodKey={payment?.paymentMethodKey}
        transactionType={payment?.transactionType}
        refundId={payment?.refundId}
        onChange={reloadData}
        onNavigate={(target) => {
          // Close the current drawer, then open the target after a short delay
          // so it shuts and re-opens rather than swapping content in place.
          setPayment(null);
          setTimeout(() => setPayment(target), 500);
        }}
      />
    </>
  );
};

export default Transactions;
